import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getStudentPopups, markPopupRead, submitSurveyResponse } from '@/lib/db'
import type { StudentPopup, SurveyQuestion } from '@/types'
import { X, Star, Trophy, Flame, PartyPopper, Rocket, Heart, ThumbsUp, Brain, BookOpen, AlertTriangle, Info } from 'lucide-react'

const ICON_MAP: Record<string, any> = {
  trophy: Trophy, star: Star, fire: Flame, party: PartyPopper, rocket: Rocket,
  heart: Heart, thumbsup: ThumbsUp, brain: Brain, books: BookOpen,
  warning: AlertTriangle, info: Info, clap: ThumbsUp,
}

const POPUP_STYLES: Record<string, { bg: string; border: string; iconBg: string }> = {
  message: { bg: 'bg-white', border: 'border-blue-200', iconBg: 'bg-blue-100 text-blue-600' },
  celebration: { bg: 'bg-gradient-to-br from-yellow-50 to-amber-50', border: 'border-yellow-300', iconBg: 'bg-yellow-100 text-yellow-600' },
  warning: { bg: 'bg-gradient-to-br from-red-50 to-orange-50', border: 'border-red-300', iconBg: 'bg-red-100 text-red-600' },
  survey: { bg: 'bg-gradient-to-br from-indigo-50 to-purple-50', border: 'border-indigo-300', iconBg: 'bg-indigo-100 text-indigo-600' },
}

// ✅ Componente extraído para romper el anidamiento
function SurveyQuestionItem({ q, qi, answer, onAnswer }: {
  q: SurveyQuestion
  qi: number
  answer: any
  onAnswer: (qId: string, value: any) => void
}) {
  function toggleMultiChoice(opt: string) {
    const selected = (answer || []) as string[]
    const isChecked = selected.includes(opt)
    onAnswer(q.id, isChecked ? selected.filter(s => s !== opt) : [...selected, opt])
  }

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-gray-800">
        {qi + 1}. {q.question} {q.required && <span className="text-red-400">*</span>}
      </p>

      {q.type === 'text' && (
        <textarea value={answer || ''} onChange={e => onAnswer(q.id, e.target.value)}
          rows={2} placeholder="Tu respuesta..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-indigo-300 outline-none" />
      )}

      {q.type === 'rating' && (
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} onClick={() => onAnswer(q.id, n)}
              className={`p-1 transition-transform ${(answer || 0) >= n ? 'text-yellow-400 scale-110' : 'text-gray-300'}`}>
              <Star className="w-7 h-7" fill={(answer || 0) >= n ? 'currentColor' : 'none'} />
            </button>
          ))}
        </div>
      )}

      {q.type === 'scale' && (
        <div className="flex gap-1">
          {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
            <button key={n} onClick={() => onAnswer(q.id, n)}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${answer === n ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {n}
            </button>
          ))}
        </div>
      )}

      {q.type === 'single_choice' && (
        <div className="space-y-1">
          {(q.options || []).map((opt, oi) => (
            <label key={oi} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${answer === opt ? 'bg-indigo-100 border border-indigo-300' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}`}>
              <input type="radio" name={q.id} checked={answer === opt} onChange={() => onAnswer(q.id, opt)} className="text-indigo-500" />
              <span className="text-sm text-gray-700">{opt}</span>
            </label>
          ))}
        </div>
      )}

      {q.type === 'multiple_choice' && (
        <div className="space-y-1">
          {(q.options || []).map((opt, oi) => {
            const isChecked = ((answer || []) as string[]).includes(opt)
            return (
              <label key={oi} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${isChecked ? 'bg-indigo-100 border border-indigo-300' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}`}>
                <input type="checkbox" checked={isChecked}
                  onChange={() => toggleMultiChoice(opt)}
                  className="text-indigo-500 rounded" />
                <span className="text-sm text-gray-700">{opt}</span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

function SurveyInlineForm({ survey, studentId, onComplete }: {
  survey: any; studentId: string; onComplete: () => void
}) {
  const questions: SurveyQuestion[] = survey.questions || []
  const [answers, setAnswers] = useState<Record<string, any>>({})
  const [submitting, setSubmitting] = useState(false)

  function setAnswer(qId: string, value: any) {
    setAnswers(prev => ({ ...prev, [qId]: value }))
  }

  async function handleSubmit() {
    setSubmitting(true)
    await submitSurveyResponse({ survey_id: survey.id, student_id: studentId, answers })
    setSubmitting(false)
    onComplete()
  }

  const allRequired = questions.filter(q => q.required).every(q => {
    const a = answers[q.id]
    return a !== undefined && a !== null && a !== ''
  })

  return (
    <div className="px-5 pb-5 space-y-4 max-h-[50vh] overflow-y-auto">
      {survey.description && <p className="text-sm text-gray-500">{survey.description}</p>}

      {questions.map((q, qi) => (
        <SurveyQuestionItem
          key={q.id}
          q={q}
          qi={qi}
          answer={answers[q.id]}
          onAnswer={setAnswer}
        />
      ))}

      <button onClick={handleSubmit} disabled={submitting || !allRequired}
        className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 transition-colors">
        {submitting ? 'Enviando...' : 'Enviar respuestas'}
      </button>
    </div>
  )
}

export default function StudentPopupManager() {
  const { profile } = useAuth()
  const [popups, setPopups] = useState<StudentPopup[]>([])
  const [currentPopup, setCurrentPopup] = useState<StudentPopup | null>(null)

  useEffect(() => {
    if (!profile?.id || profile.role !== 'student') return
    async function load() {
      const { data } = await getStudentPopups(profile!.id)
      if (data && data.length > 0) {
        setPopups(data)
        setCurrentPopup(data[0])
      }
    }
    load()
  }, [profile?.id])

  async function dismiss() {
    if (!currentPopup) return
    await markPopupRead(currentPopup.id)
    const remaining = popups.filter(p => p.id !== currentPopup.id)
    setPopups(remaining)
    setCurrentPopup(remaining.length > 0 ? remaining[0] : null)
  }

  if (!currentPopup) return null

  const isSurvey = currentPopup.popup_type === 'survey' && currentPopup.survey
  const style = POPUP_STYLES[currentPopup.popup_type] || POPUP_STYLES.message
  const IconComponent = ICON_MAP[currentPopup.config?.icon || 'star'] || Star

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 animate-fadeIn">
      <div className={`w-full max-w-md rounded-2xl shadow-2xl border-2 ${style.bg} ${style.border} overflow-hidden`}>
        <div className="flex items-center justify-between px-5 pt-5">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${style.iconBg}`}>
            <IconComponent className="w-6 h-6" />
          </div>
          <button onClick={dismiss} className="p-1 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4">
          <h2 className="text-xl font-bold text-gray-900 mb-2">{currentPopup.title}</h2>
          {currentPopup.message && <p className="text-gray-600">{currentPopup.message}</p>}
        </div>

        {isSurvey && currentPopup.survey ? (
          <SurveyInlineForm
            survey={currentPopup.survey}
            studentId={profile!.id}
            onComplete={dismiss}
          />
        ) : (
          <div className="px-5 pb-5">
            <button onClick={dismiss}
              className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transition-colors">
              {currentPopup.config?.button_text || 'Entendido'}
            </button>
          </div>
        )}

        {popups.length > 1 && (
          <div className="px-5 pb-3 text-center">
            <span className="text-xs text-gray-400">{popups.length - 1} mensaje{popups.length - 1 !== 1 ? 's' : ''} más</span>
          </div>
        )}
      </div>
    </div>
  )
}